import type { WorkspaceTab } from 'src/app/services/luna-workspace/luna-workspace-tabs.util';

/** Where Luna is working: on the whiteboard, or in an app (by its board item uid). */
export type LunaLocation = { kind: 'board' } | { kind: 'app'; itemUid: string };

/** `WHITEBOARD_TAB_ID`, spelled out to keep this file free of runtime imports. */
const WHITEBOARD_TAB_ID = 'whiteboard';

/**
 * Luna's location from the `luna.focus` attribute. The agent sends `target` ({kind:'app',
 * itemUid} or {kind:'board'}); a section or writing focus without one is on the board.
 * Null for no value, bad JSON or an unknown shape.
 */
export function parseLunaLocation(value: string | undefined): LunaLocation | null {
  if (!value) {
    return null;
  }
  let parsed: { reason?: unknown; target?: { kind?: unknown; itemUid?: unknown } };
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  const target = parsed?.target;
  if (target?.kind === 'app') {
    return typeof target.itemUid === 'string' && target.itemUid
      ? { kind: 'app', itemUid: target.itemUid }
      : null;
  }
  if (
    target?.kind === 'board' ||
    parsed?.reason === 'writing' ||
    parsed?.reason === 'section-start'
  ) {
    return { kind: 'board' };
  }
  return null;
}

/** The tab Luna is in: her app's tab, else the whiteboard; null before she has been anywhere. */
export function lunaTabIdFor(location: LunaLocation | null, tabs: WorkspaceTab[]): string | null {
  if (!location) {
    return null;
  }
  if (location.kind === 'app' && tabs.some((tab) => tab.id === location.itemUid)) {
    return location.itemUid;
  }
  return WHITEBOARD_TAB_ID;
}

/** The "Luna is on the …" card in the Luna panel. */
export interface LunaElsewhere {
  tab: WorkspaceTab;
  title: string;
  detail: string;
}

/** What Luna is doing in an app, by its app key, for the card's second line. */
const APP_DETAIL: Record<string, string> = {
  'code-playground': 'Working on the code',
  tiptap: 'Working in the notes',
  presentation: 'Looking at the slides',
  'investing-lab': 'Working in the investing lab',
};

/**
 * The "Luna is on the …" card: shown while Luna works in a tab the student is not looking at
 * (in split view both visible tabs count as looked at). Null otherwise, or before she has
 * been anywhere. `sectionTitle` is her latest whiteboard section, if any.
 */
export function lunaElsewhereFor(
  lunaTabId: string | null,
  activeTabIds: string[],
  tabs: WorkspaceTab[],
  sectionTitle: string | null,
): LunaElsewhere | null {
  if (!lunaTabId || activeTabIds.includes(lunaTabId)) {
    return null;
  }
  const tab = tabs.find((t) => t.id === lunaTabId);
  if (!tab) {
    return null;
  }
  const detail =
    tab.kind === 'whiteboard'
      ? sectionTitle || 'Writing on the board'
      : (tab.kind === 'app' && APP_DETAIL[tab.appKey ?? '']) || 'Working in this app';
  return { tab, title: `Luna is on the ${tab.label}`, detail };
}
